import type { Feature, GeoJsonProperties, Geometry } from 'geojson';

import type { FieldDefinition, FieldValueType, VectorFeatureCollection } from '../model';
import { formatScalarValue } from '../../shared/scalarPresentation';
import { fieldDisplayLabel } from '../../joins/effectiveLayer';

export type TableSortDirection = 'ascending' | 'descending';

export interface TableSort {
  readonly field: string;
  readonly direction: TableSortDirection;
}

export interface TableColumn {
  readonly field: FieldDefinition;
  readonly width: number;
}

export interface TableRowIdentity {
  readonly sourceId: string;
  readonly sourceFeatureIndex: number;
  readonly featureId: string | number | null;
  readonly key: string;
}

const stringCollator = new Intl.Collator(undefined, {
  numeric: false,
  sensitivity: 'base',
  usage: 'sort',
});

const MINIMUM_COLUMN_WIDTH = 96;
const MAXIMUM_COLUMN_WIDTH = 240;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function columnWidth(field: FieldDefinition): number {
  const typeWidth =
    field.type === 'boolean'
      ? 104
      : field.type === 'integer' || field.type === 'number'
        ? 128
        : 176;
  const headingWidth = fieldDisplayLabel(field).length * 7.5 + 42;
  return clamp(Math.max(typeWidth, headingWidth), MINIMUM_COLUMN_WIDTH, MAXIMUM_COLUMN_WIDTH);
}

export function createTableColumns(fields: readonly FieldDefinition[]): readonly TableColumn[] {
  return fields.map((field) => ({ field, width: columnWidth(field) }));
}

export function nextTableSort(current: TableSort | null, field: string): TableSort | null {
  if (current?.field !== field) return { field, direction: 'ascending' };
  if (current.direction === 'ascending') return { field, direction: 'descending' };
  return null;
}

function propertyValue(
  collection: VectorFeatureCollection,
  featureIndex: number,
  field: string,
): unknown {
  return collection.features[featureIndex]?.properties?.[field];
}

function valueRank(value: unknown): number {
  if (typeof value === 'boolean') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return 1;
  if (typeof value === 'string') return 2;
  if (Array.isArray(value)) return 3;
  if (typeof value === 'object') return 4;
  return 5;
}

function compareSameRank(left: unknown, right: unknown, type: FieldValueType): number {
  if (
    (type === 'integer' || type === 'number') &&
    typeof left === 'number' &&
    Number.isFinite(left) &&
    typeof right === 'number' &&
    Number.isFinite(right)
  ) {
    return left - right;
  }
  if (type === 'boolean' && typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }
  if (
    (type === 'string' || type === 'date') &&
    typeof left === 'string' &&
    typeof right === 'string'
  ) {
    return stringCollator.compare(left, right);
  }

  const rank = valueRank(left) - valueRank(right);
  if (rank !== 0) return rank;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right);
  if (typeof left === 'string' && typeof right === 'string') {
    return stringCollator.compare(left, right);
  }

  // Complex values share one restrained unsupported presentation. Keeping them tied
  // preserves source order and avoids expensive or misleading object interpretation.
  return 0;
}

function compareValues(
  left: unknown,
  right: unknown,
  type: FieldValueType,
  direction: TableSortDirection,
): number {
  const leftMissing = left === null || left === undefined;
  const rightMissing = right === null || right === undefined;
  if (leftMissing || rightMissing) {
    if (leftMissing && rightMissing) return 0;
    return leftMissing ? 1 : -1;
  }

  const comparison = compareSameRank(left, right, type);
  return direction === 'ascending' ? comparison : -comparison;
}

export function sortFeatureIndices(
  collection: VectorFeatureCollection,
  fields: readonly FieldDefinition[],
  sort: TableSort | null,
): readonly number[] {
  return sortRowIndices(collection.features.length, fields, sort, (rowIndex, field) =>
    propertyValue(collection, rowIndex, field),
  );
}

export function sortRowIndices(
  rowCount: number,
  fields: readonly FieldDefinition[],
  sort: TableSort | null,
  valueAt: (rowIndex: number, field: string) => unknown,
): readonly number[] {
  const indices = Array.from({ length: rowCount }, (_, index) => index);
  if (!sort) return indices;

  const field = fields.find((candidate) => candidate.name === sort.field);
  if (!field) return indices;

  indices.sort((leftIndex, rightIndex) => {
    const comparison = compareValues(
      valueAt(leftIndex, field.name),
      valueAt(rightIndex, field.name),
      field.type,
      sort.direction,
    );
    return comparison === 0 ? leftIndex - rightIndex : comparison;
  });
  return indices;
}

export function tableSourceRowKey(sourceId: string, sourceRowIndex: number): string {
  return `${sourceId}:row:${sourceRowIndex}`;
}

function featureId(feature: Feature<Geometry | null, GeoJsonProperties>): string | number | null {
  return typeof feature.id === 'string' || typeof feature.id === 'number' ? feature.id : null;
}

export function tableRowIdentity(
  sourceId: string,
  feature: Feature<Geometry | null, GeoJsonProperties>,
  sourceFeatureIndex: number,
): TableRowIdentity {
  const id = featureId(feature);
  return {
    sourceId,
    sourceFeatureIndex,
    featureId: id,
    key:
      id === null
        ? `${sourceId}:index:${sourceFeatureIndex}`
        : `${sourceId}:id:${typeof id}:${String(id)}:index:${sourceFeatureIndex}`,
  };
}

export const formatTableCell = formatScalarValue;
